<script setup>
import { computed } from 'vue'
import dayjs from 'dayjs'
import { getI18n } from '@/plugins/i18n'
import { useChips } from '@/composables/chips'

import ListItem from '@/components/shared/ListItem.vue'
import ChipTemplate from '@/components/shared/ChipTemplate.vue'

const props = defineProps({
  event: { type: Object, required: true },
  label: { type: String, required: true },
  subtitle: { type: String, default: null },
  avatar: { type: String, default: null },
  avatarIcon: { type: String, default: 'mdi-account' },
  color: { type: String, default: null },
  showStatus: { type: Boolean, default: false },
  canApprove: { type: Boolean, default: false },
  editMode: { type: Boolean, default: false },
})

const emit = defineEmits(['approve', 'revert', 'edit', 'delete'])

const i18n = getI18n()
const { getVacationStatusChips } = useChips()

const dateRange = computed(() => {
  const start = dayjs(props.event.start)
  const end = dayjs(props.event.end)
  return start.isSame(end, 'day')
    ? start.format('DD/MM/YY')
    : `${start.format('DD/MM/YY')} → ${end.format('DD/MM/YY')}`
})

const isPending = computed(() => props.showStatus && !props.event.confirmed)
</script>
<template>
  <q-item
    class="q-py-sm q-pl-sm q-pr-md"
    :clickable="editMode"
    @click="editMode && emit('edit', event)"
  >
    <list-item
      :avatar="avatar"
      :avatar-icon="avatarIcon"
      :label="label"
      dense
    >
      <template v-if="subtitle" #caption>
        <span>
          {{ subtitle }}
          <q-icon
            v-if="color"
            name="mdi-circle"
            :style="{ color }"
            size="0.5rem"
            class="q-ml-xs"
          />
        </span>
      </template>
      <template #right>
        <div class="row items-center no-wrap q-gutter-x-sm">
          <div class="column items-end">
            <q-item-label>{{ dateRange }}</q-item-label>
            <chip-template
              v-if="showStatus"
              :values="getVacationStatusChips(event.confirmed)"
            />
          </div>
          <q-btn
            v-if="isPending && canApprove"
            flat
            round
            dense
            icon="mdi-check"
            color="positive"
            :title="i18n.t('common.approve')"
            @click.stop="emit('approve', event)"
          />
          <q-btn
            v-if="showStatus && event.confirmed && canApprove"
            flat
            round
            dense
            icon="mdi-undo-variant"
            color="grey-7"
            :title="i18n.t('common.restore')"
            @click.stop="emit('revert', event)"
          />
          <q-btn
            v-if="editMode"
            style="padding: 0;"
            size="md"
            flat
            rounded
            color="negative"
            icon="mdi-minus-circle-outline"
            @click.stop="emit('delete', event)"
          />
        </div>
      </template>
    </list-item>
  </q-item>
</template>
